import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, labelFor, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Megaphone, Archive, ChevronLeft, ChevronRight } from 'lucide-react';

const ANN_TYPES = ['HOUSE_NOTICE', 'SERVICE_CHANGE', 'MAINTENANCE_NOTICE', 'MEDICATION_NOTICE', 'GENERAL'];
const VISIBILITY = ['BRANCH', 'HOUSE', 'PARTICIPANT', 'PORTAL'];
const TYPE_COLORS = { HOUSE_NOTICE: 'bg-blue-100 text-blue-700', SERVICE_CHANGE: 'bg-amber-100 text-amber-700', MAINTENANCE_NOTICE: 'bg-purple-100 text-purple-700', MEDICATION_NOTICE: 'bg-red-100 text-red-700', GENERAL: 'bg-slate-100 text-slate-700' };

export default function AnnouncementsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [visFilter, setVisFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (typeFilter !== 'all') params.append('announcementType', typeFilter);
      if (visFilter !== 'all') params.append('visibility', visFilter);
      const { data } = await api.get(`/engagement/announcements?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [page, typeFilter, visFilter]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async (id) => {
    try { await api.post(`/engagement/announcements/${id}/archive`); toast.success('Archived'); load(); }
    catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
  };

  const handleSave = async (form) => {
    try {
      if (editing) { await api.put(`/engagement/announcements/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/engagement/announcements', form); toast.success('Created'); }
      setShowCreate(false); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail?.message || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Megaphone className="w-6 h-6" />Announcements</h1>
        <Button onClick={() => { setEditing(null); setShowCreate(true); }} data-testid="new-announcement-btn"><Plus className="w-4 h-4 mr-2" />New</Button>
      </div>

      <div className="flex gap-3">
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{ANN_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={visFilter} onValueChange={v => { setVisFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="vis-filter"><SelectValue placeholder="Visibility" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{VISIBILITY.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <div className="space-y-2">
          {items.map(a => (
            <Card key={a.id} className={a.status === 'ARCHIVED' ? 'opacity-50' : ''}>
              <CardContent className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className={TYPE_COLORS[a.announcementType] || 'bg-slate-100'}>{a.announcementType?.replace(/_/g, ' ')}</Badge>
                    <Badge variant="outline">{a.visibility}</Badge>
                    {a.status === 'ARCHIVED' && <Badge variant="destructive">Archived</Badge>}
                  </div>
                  <p className="font-medium mt-1">{a.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{a.body}</p>
                  <div className="text-xs text-muted-foreground mt-1">
                    <span>From: {a.activeFrom?.slice(0, 10)}</span>
                    {a.activeTo && <span className="ml-3">To: {a.activeTo?.slice(0, 10)}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {a.status !== 'ARCHIVED' && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setShowCreate(true); }}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleArchive(a.id)} data-testid={`archive-${a.id}`}><Archive className="w-3 h-3" /></Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && <p className="text-center text-muted-foreground py-8">No announcements</p>}
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{total} total</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm py-1">Page {page}</span>
          <Button size="sm" variant="outline" disabled={items.length < 20} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Dialog open={showCreate} onOpenChange={() => { setShowCreate(false); setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Announcement' : 'New Announcement'}</DialogTitle></DialogHeader>
          <AnnouncementForm announcement={editing} onSave={handleSave} onCancel={() => { setShowCreate(false); setEditing(null); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AnnouncementForm({ announcement, onSave, onCancel }) {
  const [participants, setParticipants] = useState([]);
  const [form, setForm] = useState(announcement ? {
    title: announcement.title, body: announcement.body, announcementType: announcement.announcementType,
    visibility: announcement.visibility, activeFrom: announcement.activeFrom?.slice(0, 10) || '', activeTo: announcement.activeTo?.slice(0, 10) || '',
    participantId: announcement.participantId || '', houseId: announcement.houseId || '',
  } : {
    title: '', body: '', announcementType: 'GENERAL', visibility: 'BRANCH', activeFrom: new Date().toISOString().slice(0, 10), activeTo: '', participantId: '', houseId: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/participants?page=1&pageSize=50').then(r => setParticipants(r.data.items || r.data.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-3">
      <div><Label>Title</Label><Input data-testid="ann-title" value={form.title} onChange={e => set('title', e.target.value)} /></div>
      <div><Label>Body</Label><Textarea data-testid="ann-body" value={form.body} onChange={e => set('body', e.target.value)} rows={4} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label>
          <Select value={form.announcementType} onValueChange={v => set('announcementType', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{ANN_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Visibility</Label>
          <Select value={form.visibility} onValueChange={v => set('visibility', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{VISIBILITY.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Active From</Label><Input type="date" value={form.activeFrom} onChange={e => set('activeFrom', e.target.value)} /></div>
        <div><Label>Active To (optional)</Label><Input type="date" value={form.activeTo} onChange={e => set('activeTo', e.target.value)} /></div>
      </div>
      {form.visibility === 'PARTICIPANT' && (
        <div><Label>Participant</Label>
          <AsyncSearchSelect
            endpoint="/participants"
            value={form.participantId}
            onChange={v => set('participantId', v)}
            getOptionLabel={participantLabel}
            selectedLabel={labelFor(participants, form.participantId, participantLabel)}
            placeholder="Select participant"
            searchPlaceholder={PARTICIPANT_SEARCH_HINT}
            emptyText="No participants match that search"
            testId="announcement-participant"
          />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="save-announcement-btn" onClick={() => onSave(form)} disabled={!form.title || !form.body || !form.activeFrom}>Save</Button>
      </div>
    </div>
  );
}
